import type { STLTessJson } from 'contracts/tess';
import {
  Box3,
  BufferAttribute,
  BufferGeometry,
  EdgesGeometry,
  Vector3,
  type Float32BufferAttribute,
} from 'three';

import { SimpleProgressController } from '@frontend/controllers';
import type { ProgressSignal } from '@frontend/controllers';
import {
  WaitUntilSetController,
  wait,
  addEventListenerAndCollectRemovers,
  runRemoveEventListeners,
} from '@frontend/utils';

// eslint-disable-next-line no-restricted-imports
import { PART_TYPES } from '../../constants';
// eslint-disable-next-line no-restricted-imports
import { disposeThreeJS } from '../../geom-utils';
// eslint-disable-next-line no-restricted-imports
import type {
  AsyncLoadable,
  SyncLoadable,
} from '../../renderer/loadingPriorityQueue';
import { fillRepeatedPattern } from '../step/utils';

export type CloneableSTLGeometry = {
  type: PART_TYPES.STL;
  positions: Float32Array[];
  normals: Float32Array[];
};

const faceSplitCount = 500;
export const maxSTLTriangles = 21845; //Math.floor(65535 / 3); //per buffer max vertices for the triangles
export const maxSTLVertices = maxSTLTriangles * 3; //= 65535
//export const maxSTLPositions = maxSTLVertices * 3; //= 196605 //max len per `position` buffer attribute
const positionsPerTriangle = 9;

export class STLGeometry implements AsyncLoadable, SyncLoadable {
  public static createProgress(
    data: Pick<STLTessJson, 'Faces'>,
    id: string,
    name: string,
  ): SimpleProgressController | undefined {
    const todoCount =
      (data.Faces[0] || []).length + (data.Faces[1] || []).length;
    if (todoCount > 0) {
      return new SimpleProgressController({
        id,
        description: `Loading Geometry for Part ${name || id}`,
        todoCount,
        expectedMs: Math.ceil(0.1 * todoCount),
        start: false,
      });
    }
  }

  public readonly type: PART_TYPES.STL = PART_TYPES.STL;
  public readonly geometries: BufferGeometry[] = [];
  public readonly wireGeometries: EdgesGeometry[] = [];
  private _boundingBox?: Box3; //axis aligned bounding box

  private verticesJson?: STLTessJson['Faces'][0];
  private normalsJson?: STLTessJson['Faces'][1];

  private resumeController?: WaitUntilSetController<void>;
  private loaded?: Promise<number>; //presence indicates this.isLoading. Resolves when loaded
  private disposeFns: (() => void)[] = [];
  private isDisposed?: boolean;

  // this requires a 2x sized json file from freecad since it repeats
  // mesh points and doesnt use connectivity for display purposes
  // eventually will want to bring parser to js side to minimize data
  // transfer, but for now keep it in its format so it all comes from
  // the same place
  constructor(
    data: Pick<STLTessJson, 'Faces'>,
    private _progress?: SimpleProgressController, //create progress in advance so it can be composed and composedProgress closed. Use STLGeometry.createProgress()
    private readonly signal?: AbortSignal, //AbortSignal to abort loading
  ) {
    this.verticesJson = data.Faces[0];
    this.normalsJson = data.Faces[1];
    if (signal) {
      addEventListenerAndCollectRemovers(
        this.disposeFns,
        signal,
        'abort',
        () => {
          this.dispose();
        },
      );
    }
  }

  public static fromJSON(
    data: STLTessJson,
    id: string,
    name: string,
    signal?: AbortSignal, //AbortSignal to abort loading
  ): [STLGeometry, ProgressSignal<SimpleProgressController>?] {
    const progress = STLGeometry.createProgress(data, id, name);
    return [new STLGeometry(data, progress, signal), progress?.signal];
  }

  public static fromCloneable(data: CloneableSTLGeometry): STLGeometry {
    const stlGeometry = new STLGeometry(
      { Faces: [[], []] }, //dummy data (will load geometry below)
    );
    const min = new Vector3();
    const max = new Vector3();
    for (const [i, ps] of data.positions.entries()) {
      const g = new BufferGeometry();
      g.setAttribute('position', new BufferAttribute(ps, 3))
        .setAttribute('normal', new BufferAttribute(data.normals[i], 3))
        .computeBoundingBox();
      const boundingBox = g.boundingBox as Box3;
      stlGeometry.geometries.push(g);
      stlGeometry.wireGeometries.push(new EdgesGeometry(g));
      min.min(boundingBox.min);
      max.max(boundingBox.max);
    }
    stlGeometry._boundingBox = new Box3(min, max); //merged boundingBox
    delete stlGeometry.normalsJson;
    delete stlGeometry.verticesJson;
    return stlGeometry;
  }

  public dispose() {
    if (this.isDisposed) return;
    this.isDisposed = true;
    runRemoveEventListeners(this.disposeFns);
    this.abortLoad();
    disposeThreeJS(this.geometries, this.wireGeometries);
    delete this._boundingBox;
    for (const a of [this.verticesJson, this.normalsJson]) {
      if (a) a.length = 0;
    }
  }

  public get boundingBox(): Box3 {
    if (!this.isLoaded) {
      throw new Error('STL geometry is not loaded yet');
    }
    return this._boundingBox as Box3;
  }

  public get faceVerticesCount(): number {
    return (
      this.geometries.reduce(
        (count, g) => count + g.attributes.position.array.length,
        0,
      ) / 3
    );
  }

  public get edgeVerticesCount(): number {
    return (
      this.wireGeometries.reduce(
        (count, g) => count + g.attributes.position.array.length,
        0,
      ) / 3
    );
  }

  //geometryVerticesCount = edgeVerticesCount + faceVerticesCount;
  public get geometryVerticesCount(): number {
    return this.faceVerticesCount + this.edgeVerticesCount;
  }

  public get progress(): ProgressSignal<SimpleProgressController> | undefined {
    return this._progress?.signal;
  }

  public get isLoaded(): boolean {
    return (!this.verticesJson && !this.normalsJson) || !this.wireGeometries;
  }

  public get isLoading(): boolean {
    return !!this.loaded;
  }

  public pause() {
    if (!this.isLoaded && !this.resumeController) {
      this.resumeController = new WaitUntilSetController(this.signal);
    }
  }

  public get isPaused(): boolean {
    return !!this.resumeController;
  }

  public resume() {
    if (this.resumeController) {
      this.resumeController.set();
      delete this.resumeController;
    }
  }

  public abortLoad() {
    if (this.loaded) {
      this.pause(); //force pause so we can reject
      this.loaded.catch(() => Promise.resolve()); //the promise's rejection should be handled at least once
      delete this.loaded;
    }
    if (this.resumeController) {
      this.resumeController.setReject();
      delete this.resumeController;
    }
    if (this._progress) {
      this._progress.done();
    }
  }

  //load(): Alternative to this.loadAsync() and loadCloneable()
  //* Not preferred for geometry with many faces and edges because this is a sync method and it will block UI.
  //* loadCloneable() is also synchronous, but is faster
  // TODO: on reinterrogation optimize this to not use redundant vertices
  // * This TODO is from Dana. Need to discuss with him. I suspect he means some vertices are not shared between triangles,
  //   which is the norm for webgl/opengl pipelines. He could have meant there is an opportunity to reduce the size of
  //   the STLTessJson payload... but even so, they must be duplicated when creating the 'positions'. But maybe he had
  //   something else in mind?
  public load(): void {
    if (this.normalsJson && this.verticesJson && !this.signal?.aborted) {
      //!isLoaded && !aborted
      if (this.loaded) {
        //isLoading is true because this.loaded promise exists
        throw new Error(
          'Cannot load STLGeometry synchronously that already started async loading',
        );
      }
      try {
        this._progress?.start();
        const chunkCount = Math.ceil(this.normalsJson.length / maxSTLTriangles);
        const min = new Vector3();
        const max = new Vector3();
        for (let chunkNum = 0; chunkNum < chunkCount; chunkNum++) {
          const start = chunkNum * maxSTLTriangles;
          const endPlusOne =
            chunkNum === chunkCount - 1
              ? this.normalsJson.length
              : start + maxSTLTriangles;
          const len = positionsPerTriangle * (endPlusOne - start); //3 vertices per face and 3 positions per vertex = 9 (Always <= 21845 * 9 = 196605)

          const positions: Float32Array = new Float32Array(len);
          const normals: Float32Array = new Float32Array(len);
          //* Note: This is an expensive loop. If possible use fromCloneable() to save time.
          for (let i = start; i < endPlusOne; i++) {
            const triangleVerts = this.verticesJson[i].flatMap((_) => _);
            const bufOffset = positionsPerTriangle * (i - start);
            positions.set(triangleVerts, bufOffset);
            fillRepeatedPattern(this.normalsJson[i], normals, bufOffset, 3);
          }

          const g = new BufferGeometry()
            .setAttribute('position', new BufferAttribute(positions, 3))
            .setAttribute('normal', new BufferAttribute(normals, 3));
          g.computeVertexNormals();
          g.computeBoundingBox();
          const boundingBox = g.boundingBox as Box3;
          min.min(boundingBox.min);
          max.max(boundingBox.max);
          this.geometries.push(g);
          this.wireGeometries.push(new EdgesGeometry(g));
        }
        this._boundingBox = new Box3(min, max); //merged boundingBox
      } finally {
        if (this._progress) {
          this._progress.done();
          delete this._progress;
        }
        delete this.verticesJson;
        delete this.normalsJson;
        this.resume(); //cleanup resumeController if it exists
      }
    }
  }

  //loadAync(): Preferred way to load geometry. With loadAsync:
  //* loading can be paused/resumed with this.pause() and this.resume() respectively
  //* loading runs in 50ms chunks so the UI is not blocked
  public loadAsync(lastPause: number = performance.now()): Promise<number> {
    if (
      !this._progress ||
      !this.normalsJson ||
      (!this.verticesJson && this.signal?.aborted)
    ) {
      //isLoaded === true || aborted
      return Promise.resolve(lastPause);
    }
    if (!this.loaded) {
      //Not loaded, and not isLoading
      this.loaded = (async (): Promise<number> => {
        try {
          if (this.normalsJson && this.verticesJson) {
            this._progress?.start();

            const chunkCount = Math.ceil(
              this.normalsJson.length / maxSTLTriangles,
            );
            const min = new Vector3();
            const max = new Vector3();
            for (let chunkNum = 0; chunkNum < chunkCount; chunkNum++) {
              const start = chunkNum * maxSTLTriangles;
              const endPlusOne =
                chunkNum === chunkCount - 1
                  ? this.normalsJson.length
                  : start + maxSTLTriangles;
              const len = positionsPerTriangle * (endPlusOne - start); //3 vertices per face and 3 positions per vertex = 9 (Always <= 21845 * 9 = 196605)

              const positions: Float32Array = new Float32Array(len);
              const normals: Float32Array = new Float32Array(len);
              //* Note: This is an expensive loop. If possible use fromCloneable() to save time.
              for (let i = start; i < endPlusOne; i++) {
                //Every faceSplitCount faces, consider pausing (if paused) or deferring after 50ms of continuous work so we don't block UI
                //Note: It would be nice to make this if block a macro or fn. I am not using fn for performance reasons
                //because the fn would have to be async and would always defer each loop. But when inlined here it is only defer'ing when needed
                //Can't use macro because they do not exist in js/ts yet.
                if (i % faceSplitCount === 0) {
                  if (this.resumeController) {
                    await this.resumeController.waitUntilSet;
                    //eslint-disable-next-line no-param-reassign
                    lastPause = performance.now();
                  }
                  const now = performance.now();
                  if (now - lastPause > 50) {
                    await wait();
                    //eslint-disable-next-line no-param-reassign
                    lastPause = now;
                  }
                  if (this.signal?.aborted) {
                    break;
                  }
                  this._progress?.addProgress(faceSplitCount);
                }

                const triangleVerts = this.verticesJson[i].flatMap((_) => _);
                const bufOffset = positionsPerTriangle * (i - start);
                positions.set(triangleVerts, bufOffset);
                fillRepeatedPattern(this.normalsJson[i], normals, bufOffset, 3);
              }

              const g = new BufferGeometry()
                .setAttribute('position', new BufferAttribute(positions, 3))
                .setAttribute('normal', new BufferAttribute(normals, 3));
              g.computeVertexNormals();
              g.computeBoundingBox();
              const boundingBox = g.boundingBox as Box3;
              min.min(boundingBox.min);
              max.max(boundingBox.max);
              this.geometries.push(g);
              this.wireGeometries.push(new EdgesGeometry(g));
            }
            this._boundingBox = new Box3(min, max); //merged boundingBox
          }
        } finally {
          if (this._progress) {
            this._progress.done();
            delete this._progress;
          }
          delete this.loaded;
          delete this.verticesJson;
          delete this.normalsJson;
          this.resume(); //cleanup resumeController if it exists
        }
        return lastPause;
      })();
    } else if (this.isPaused) {
      this.resume();
    }
    return this.loaded; //loadedPromise
  }

  public get positions(): Float32Array[] {
    return this.geometries.map(
      (g) => (g.attributes.position as Float32BufferAttribute)?.array,
    ) as Float32Array[];
  }

  public get normals(): Float32Array[] {
    return this.geometries.map(
      (g) => (g.attributes.normal as Float32BufferAttribute)?.array,
    ) as Float32Array[];
  }

  public toCloneable(): CloneableSTLGeometry {
    if (!this.isLoaded) {
      throw new Error('Cannot convert to cloneable if not loaded yet.');
    }
    return {
      type: PART_TYPES.STL,
      positions: this.positions.map((p) => p.slice()),
      normals: this.normals.map((n) => n.slice()),
    };
  }

  public loadCloneable(data: CloneableSTLGeometry): void {
    if (this.isLoaded || this.isLoading) {
      throw new Error('Cannot load cloneable if loaded or loading.');
    } else {
      const min = new Vector3();
      const max = new Vector3();
      for (const [i, ps] of data.positions.entries()) {
        const g = new BufferGeometry();
        g.setAttribute('position', new BufferAttribute(ps, 3))
          .setAttribute('normal', new BufferAttribute(data.normals[i], 3))
          .computeBoundingBox();
        const boundingBox = g.boundingBox as Box3;
        this.geometries.push(g);
        this.wireGeometries.push(new EdgesGeometry(g));
        min.min(boundingBox.min);
        max.max(boundingBox.max);
      }
      this._boundingBox = new Box3(min, max); //merged boundingBox
      delete this.verticesJson;
      delete this.normalsJson;
    }
  }
}
